import { buildFilterDropdownOptions } from '@app/v2/shared/utils/filter-dropdown-options.util'
import { InvitationCollectionActionTaken } from '../enums/invitation-collection.enum'
import { INVITATION_CHANNEL_TO_API, isInvitationChannelComingSoon } from '../utils/invitation-channel-status.util'
import {
  InvitationCollectionActionTakenFilter,
  InvitationCollectionActionTakenValue,
  InvitationCollectionChannelFilter,
  InvitationCollectionFilterDropdown,
  InvitationCollectionFilterGroup,
  InvitationCollectionFilterKey,
  InvitationCollectionFilterOption,
  InvitationCollectionFilters,
  InvitationCollectionFilterSection,
  InvitationCollectionInviteStatusFilter,
  InvitationCollectionSourceFilter,
  InvitationTrackerCardFilter
} from './invitation-collection-filter.type'
import { InvitationCollectionQueryFilters, InvitationCollectionSourceEvent, InvitationTrackerFilterCardKey } from './invitation-tracker.type'

export class InvitationCollectionFilterModel {
  static readonly ALL_VALUE = 'all'

  static readonly STATIC_SOURCE_OPTIONS: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>> = [
    { value: InvitationCollectionFilterModel.ALL_VALUE, label: 'All sources' },
    { value: 'imported', label: 'Imported' }
  ]

  static readonly CHANNEL_OPTIONS: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionChannelFilter>> = [
    { value: InvitationCollectionFilterModel.ALL_VALUE, label: 'All channels' },
    { value: 'email', label: 'Email' },
    { value: 'whatsapp', label: 'WhatsApp' },
    //TODO: Remove this once SMS is supported
    { value: 'sms', label: 'SMS', disabled: isInvitationChannelComingSoon('sms') }
  ]

  static readonly INVITE_STATUS_OPTIONS: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionInviteStatusFilter>> = [
    { value: InvitationCollectionFilterModel.ALL_VALUE, label: 'All statuses' },
    { value: 'queued', label: 'Queued' },
    { value: 'pending', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'payment_failed', label: 'Payment failed' },
    { value: 'failed', label: 'Failed' },
    { value: 'bounced', label: 'Bounced' },
    { value: 'opt_out', label: 'Opted out' }
  ]

  static readonly EMAIL_ONLY_INVITE_STATUSES: ReadonlySet<InvitationCollectionInviteStatusFilter> = new Set(['bounced', 'opt_out'])

  static readonly ACTION_TAKEN_OPTIONS: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionActionTakenFilter>> = [
    { value: InvitationCollectionFilterModel.ALL_VALUE, label: 'All' },
    { value: 'not_invited', label: 'Not invited yet' },
    { value: InvitationCollectionActionTaken.REGISTERED, label: 'Registered' },
    { value: InvitationCollectionActionTaken.PENDING, label: 'Pending' },
    { value: InvitationCollectionActionTaken.DECLINED, label: 'Declined' },
    { value: InvitationCollectionActionTaken.UNREACHABLE, label: 'Unreachable' },
    { value: 'needs_attention', label: 'Needs attention' }
  ]

  static readonly CARD_FILTERS: Readonly<Record<InvitationTrackerFilterCardKey, InvitationTrackerCardFilter>> = {
    total_pending: { key: 'actionTaken', value: InvitationCollectionActionTaken.PENDING },
    total_registered: { key: 'actionTaken', value: InvitationCollectionActionTaken.REGISTERED },
    total_declined: { key: 'actionTaken', value: InvitationCollectionActionTaken.DECLINED },
    total_bounced: { key: 'inviteStatus', value: 'bounced' },
    total_opt_out: { key: 'inviteStatus', value: 'opt_out' }
  }

  static readonly CARD_KEYS = Object.keys(InvitationCollectionFilterModel.CARD_FILTERS) as ReadonlyArray<InvitationTrackerFilterCardKey>

  static readonly NOT_INVITED_QUERY_VALUE = 'NULL'
  static readonly NEEDS_ATTENTION_QUERY_VALUE = InvitationCollectionActionTaken.NEEDS_ATTENTION

  /** Display order and chrome of the filter groups (desktop dropdowns and mobile drawer sections). */
  static readonly GROUPS: ReadonlyArray<InvitationCollectionFilterGroup> = [
    { key: 'source', placeholder: 'Source', ariaLabel: 'Filter by source', widthPx: '200px' },
    { key: 'channel', placeholder: 'Invited via', ariaLabel: 'Filter by invited via', widthPx: '160px' },
    { key: 'inviteStatus', placeholder: 'Invitation status', shortPlaceholder: 'Status', ariaLabel: 'Filter by invitation status', widthPx: '200px' },
    { key: 'actionTaken', placeholder: 'Action taken', shortPlaceholder: 'Action', ariaLabel: 'Filter by action taken', widthPx: '180px' }
  ]

  /**
   * Create default filters with every group set to "All".
   * @returns {InvitationCollectionFilters} - The default filters.
   */
  static createDefault(): InvitationCollectionFilters {
    return { source: [], channel: [], inviteStatus: [], actionTaken: [] }
  }

  /**
   * Build the source options: the static options followed by one option per source event (deduplicated by event id).
   * @param events {ReadonlyArray<InvitationCollectionSourceEvent>} - The source events from the collection response.
   * @returns {InvitationCollectionFilterOption<InvitationCollectionSourceFilter>[]} - The source options.
   */
  static buildSourceOptions(events: ReadonlyArray<InvitationCollectionSourceEvent>): InvitationCollectionFilterOption<InvitationCollectionSourceFilter>[] {
    const seenEventIds = new Set<number>()
    const eventOptions = events
      .filter((event) => {
        if (seenEventIds.has(event.event_id)) return false
        seenEventIds.add(event.event_id)
        return true
      })
      .map((event) => ({ value: event.event_id, label: event.event_name }))

    return [...InvitationCollectionFilterModel.STATIC_SOURCE_OPTIONS, ...eventOptions]
  }

  /**
   * Drop source values that are no longer source options (the group returns to "All" when none remain); returns the same object otherwise.
   * @param filters {InvitationCollectionFilters} - The filters.
   * @param sourceOptions {ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>} - The source options (static + events).
   * @returns {InvitationCollectionFilters} - The filters with valid source values.
   */
  static withValidSource(
    filters: InvitationCollectionFilters,
    sourceOptions: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>
  ): InvitationCollectionFilters {
    const source = filters.source.filter((value) => sourceOptions.some((option) => option.value === value))
    if (source.length === filters.source.length) return filters
    return { ...filters, source }
  }

  /**
   * Map the filters to the collection API query; each field is a list of values and "All" groups are omitted.
   * @param filters {InvitationCollectionFilters} - The applied filters.
   * @returns {InvitationCollectionQueryFilters} - The query filters.
   */
  static toQueryFilters(filters: InvitationCollectionFilters): InvitationCollectionQueryFilters {
    const query: InvitationCollectionQueryFilters = {}

    if (filters.source.length) query.source = [...filters.source]
    if (filters.channel.length) query.channel = filters.channel.map((channel) => INVITATION_CHANNEL_TO_API[channel])
    if (filters.inviteStatus.length) query.invite_status = [...filters.inviteStatus]
    if (filters.actionTaken.length) query.action_taken = filters.actionTaken.map((value) => InvitationCollectionFilterModel._toActionTakenQueryValue(value))

    return query
  }

  /**
   * Whether two sets of filters produce the same collection API query.
   * @param a {InvitationCollectionFilters} - The first filters.
   * @param b {InvitationCollectionFilters} - The second filters.
   * @returns {boolean} - Whether the queries match.
   */
  static isSameQuery(a: InvitationCollectionFilters, b: InvitationCollectionFilters): boolean {
    const queryA = InvitationCollectionFilterModel.toQueryFilters(a)
    const queryB = InvitationCollectionFilterModel.toQueryFilters(b)

    return (
      InvitationCollectionFilterModel._isSameValues(queryA.source, queryB.source) &&
      InvitationCollectionFilterModel._isSameValues(queryA.channel, queryB.channel) &&
      InvitationCollectionFilterModel._isSameValues(queryA.invite_status, queryB.invite_status) &&
      InvitationCollectionFilterModel._isSameValues(queryA.action_taken, queryB.action_taken)
    )
  }

  /**
   * Count the active invitation collection filters.
   * @param filters {InvitationCollectionFilters} - The filters.
   * @returns {number} - The count of active filters.
   */
  static countActive(filters: InvitationCollectionFilters): number {
    return InvitationCollectionFilterModel.GROUPS.filter((group) => filters[group.key].length > 0).length
  }

  /**
   * Resolve the trigger label for a group: the value's label when exactly one is selected, the group name otherwise ("All" or 2+ values).
   * @param options {ReadonlyArray<InvitationCollectionFilterOption>} - The options.
   * @param values {ReadonlyArray<string | number>} - The selected values.
   * @param placeholder {string} - The group name.
   * @returns {string} - The label.
   */
  static getLabel(options: ReadonlyArray<InvitationCollectionFilterOption>, values: ReadonlyArray<string | number>, placeholder: string): string {
    if (values.length !== 1) return placeholder
    return options.find((option) => option.value === values[0])?.label ?? placeholder
  }

  /**
   * Toggle one option of a group: "All" clears the group, a specific value is added or removed (removing the last returns the group to "All").
   * A channel change that excludes email also drops the email-only invitation statuses.
   * Returns the same object when nothing changes, or when the value is not a selectable option of that group.
   * @param filters {InvitationCollectionFilters} - The current filters.
   * @param key {InvitationCollectionFilterKey} - The filter group.
   * @param value {string | number} - The toggled option value.
   * @param sourceOptions {ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>} - The source options (static + events).
   * @returns {InvitationCollectionFilters} - The updated filters.
   */
  static withToggledValue(
    filters: InvitationCollectionFilters,
    key: InvitationCollectionFilterKey,
    value: string | number,
    sourceOptions: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>
  ): InvitationCollectionFilters {
    const current: ReadonlyArray<string | number> = filters[key]

    if (value === InvitationCollectionFilterModel.ALL_VALUE) return current.length ? { ...filters, [key]: [] } : filters

    const isSelectableOption = InvitationCollectionFilterModel._getOptions(key, sourceOptions, filters).some((option) => option.value === value && !option.disabled)
    if (!isSelectableOption) return filters

    const next = current.includes(value) ? current.filter((selected) => selected !== value) : [...current, value]
    const updated = { ...filters, [key]: next } as InvitationCollectionFilters
    return key === 'channel' ? InvitationCollectionFilterModel._withoutEmailOnlyStatusesIfNeeded(updated) : updated
  }

  /**
   * Resolve the insights card matching the filters: only when its value is the single filter applied across every group.
   * @param filters {InvitationCollectionFilters} - The applied filters.
   * @returns {InvitationTrackerFilterCardKey | null} - The matching card, or null when none matches.
   */
  static getMatchingCardKey(filters: InvitationCollectionFilters): InvitationTrackerFilterCardKey | null {
    const appliedValueCount = InvitationCollectionFilterModel.GROUPS.reduce((count, group) => count + filters[group.key].length, 0)
    if (appliedValueCount !== 1) return null

    return (
      InvitationCollectionFilterModel.CARD_KEYS.find((cardKey) => {
        const { key, value } = InvitationCollectionFilterModel.CARD_FILTERS[cardKey]
        const values: ReadonlyArray<string | number> = filters[key]
        return values.includes(value)
      }) ?? null
    )
  }

  /**
   * Apply an insights card's filter on its own (every other group returns to "All"); clicking the already selected card clears it.
   * @param filters {InvitationCollectionFilters} - The current filters.
   * @param cardKey {InvitationTrackerFilterCardKey} - The clicked card.
   * @returns {InvitationCollectionFilters} - The updated filters.
   */
  static withCardFilter(filters: InvitationCollectionFilters, cardKey: InvitationTrackerFilterCardKey): InvitationCollectionFilters {
    const defaults = InvitationCollectionFilterModel.createDefault()
    if (InvitationCollectionFilterModel.getMatchingCardKey(filters) === cardKey) return defaults

    const cardFilter = InvitationCollectionFilterModel.CARD_FILTERS[cardKey]
    switch (cardFilter.key) {
      case 'actionTaken':
        return { ...defaults, actionTaken: [cardFilter.value] }
      case 'inviteStatus':
        return { ...defaults, inviteStatus: [cardFilter.value] }
    }
  }

  /**
   * Build the view state for every filter dropdown.
   * @param filters {InvitationCollectionFilters} - The applied filters.
   * @param sourceOptions {ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>} - The source options (static + events).
   * @returns {InvitationCollectionFilterDropdown[]} - The dropdowns.
   */
  static buildDropdowns(
    filters: InvitationCollectionFilters,
    sourceOptions: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>
  ): InvitationCollectionFilterDropdown[] {
    return InvitationCollectionFilterModel._resolveGroups(filters, sourceOptions).map(({ group, options, values, checkedValues }) => ({
      ...group,
      label: InvitationCollectionFilterModel.getLabel(options, values, group.placeholder),
      shortLabel: InvitationCollectionFilterModel.getLabel(options, values, group.shortPlaceholder ?? group.placeholder),
      isActive: values.length > 0,
      selectedCount: values.length,
      dropdownOptions: buildFilterDropdownOptions(options, checkedValues)
    }))
  }

  /**
   * Build the mobile drawer sections with every selected option flagged per group.
   * @param filters {InvitationCollectionFilters} - The draft filters.
   * @param sourceOptions {ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>} - The source options (static + events).
   * @returns {InvitationCollectionFilterSection[]} - The sections.
   */
  static buildSections(
    filters: InvitationCollectionFilters,
    sourceOptions: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>
  ): InvitationCollectionFilterSection[] {
    return InvitationCollectionFilterModel._resolveGroups(filters, sourceOptions).map(({ group, options, checkedValues }) => ({
      key: group.key,
      title: group.placeholder,
      options: options.map((option) => ({ ...option, selected: checkedValues.includes(option.value) }))
    }))
  }

  /**
   * Pair every filter group with its options, selected values, and the values to check ("All" while nothing is selected).
   * @param filters {InvitationCollectionFilters} - The filters.
   * @param sourceOptions {ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>} - The source options (static + events).
   * @returns {Array<{ group: InvitationCollectionFilterGroup; options: ReadonlyArray<InvitationCollectionFilterOption>; values: ReadonlyArray<string | number>; checkedValues: ReadonlyArray<string | number> }>} - The resolved groups.
   */
  private static _resolveGroups(
    filters: InvitationCollectionFilters,
    sourceOptions: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>
  ): Array<{
    group: InvitationCollectionFilterGroup
    options: ReadonlyArray<InvitationCollectionFilterOption>
    values: ReadonlyArray<string | number>
    checkedValues: ReadonlyArray<string | number>
  }> {
    return InvitationCollectionFilterModel.GROUPS.map((group) => {
      const values: ReadonlyArray<string | number> = filters[group.key]
      return {
        group,
        options: InvitationCollectionFilterModel._getOptions(group.key, sourceOptions, filters),
        values,
        checkedValues: values.length ? values : [InvitationCollectionFilterModel.ALL_VALUE]
      }
    })
  }

  /**
   * Map an action-taken filter value to the value the collection API expects.
   * @param value {InvitationCollectionActionTakenValue} - The action-taken filter value.
   * @returns {InvitationCollectionActionTaken | 'NULL'} - The query value.
   */
  private static _toActionTakenQueryValue(value: InvitationCollectionActionTakenValue): InvitationCollectionActionTaken | 'NULL' {
    if (value === 'not_invited') return InvitationCollectionFilterModel.NOT_INVITED_QUERY_VALUE
    if (value === 'needs_attention') return InvitationCollectionFilterModel.NEEDS_ATTENTION_QUERY_VALUE
    return value
  }

  /**
   * Whether two query value lists hold the same values, ignoring order; a missing list counts as empty.
   * @param a {ReadonlyArray<string | number> | undefined} - The first values.
   * @param b {ReadonlyArray<string | number> | undefined} - The second values.
   * @returns {boolean} - Whether the lists match.
   */
  private static _isSameValues(a: ReadonlyArray<string | number> = [], b: ReadonlyArray<string | number> = []): boolean {
    if (a.length !== b.length) return false
    const valuesB = new Set(b)
    return a.every((value) => valuesB.has(value))
  }

  /**
   * Whether the channel filter narrows to channels that do not include email ("All" includes email).
   * @param filters {InvitationCollectionFilters} - The filters.
   * @returns {boolean} - Whether email is excluded.
   */
  private static _excludesEmailChannel(filters: InvitationCollectionFilters): boolean {
    return filters.channel.length > 0 && !filters.channel.includes('email')
  }

  /**
   * Drop the email-only invitation statuses while the channel filter excludes email; returns the same object otherwise.
   * @param filters {InvitationCollectionFilters} - The filters.
   * @returns {InvitationCollectionFilters} - The filters with only statuses the selected channels can report.
   */
  private static _withoutEmailOnlyStatusesIfNeeded(filters: InvitationCollectionFilters): InvitationCollectionFilters {
    if (!InvitationCollectionFilterModel._excludesEmailChannel(filters)) return filters

    const inviteStatus = filters.inviteStatus.filter((status) => !InvitationCollectionFilterModel.EMAIL_ONLY_INVITE_STATUSES.has(status))
    if (inviteStatus.length === filters.inviteStatus.length) return filters
    return { ...filters, inviteStatus }
  }

  /**
   * Invitation status options with the email-only statuses disabled while the channel filter excludes email.
   * @param filters {InvitationCollectionFilters} - The filters.
   * @returns {ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionInviteStatusFilter>>} - The options.
   */
  private static _getInviteStatusOptions(filters: InvitationCollectionFilters): ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionInviteStatusFilter>> {
    if (!InvitationCollectionFilterModel._excludesEmailChannel(filters)) return InvitationCollectionFilterModel.INVITE_STATUS_OPTIONS

    return InvitationCollectionFilterModel.INVITE_STATUS_OPTIONS.map((option) =>
      InvitationCollectionFilterModel.EMAIL_ONLY_INVITE_STATUSES.has(option.value) ? { ...option, disabled: true } : option
    )
  }

  /**
   * Resolve the option list for a filter group.
   * @param key {InvitationCollectionFilterKey} - The filter group.
   * @param sourceOptions {ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>} - The source options (static + events).
   * @param filters {InvitationCollectionFilters} - The current filters (some options depend on other groups).
   * @returns {ReadonlyArray<InvitationCollectionFilterOption>} - The options.
   */
  private static _getOptions(
    key: InvitationCollectionFilterKey,
    sourceOptions: ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>,
    filters: InvitationCollectionFilters
  ): ReadonlyArray<InvitationCollectionFilterOption> {
    switch (key) {
      case 'source':
        return sourceOptions
      case 'channel':
        return InvitationCollectionFilterModel.CHANNEL_OPTIONS
      case 'inviteStatus':
        return InvitationCollectionFilterModel._getInviteStatusOptions(filters)
      case 'actionTaken':
        return InvitationCollectionFilterModel.ACTION_TAKEN_OPTIONS
    }
  }
}
